import {
  amber,
  blue,
  blueGrey,
  cyan,
  deepPurple,
  green,
  indigo,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow
} from '@mui/material/colors'
import { Phase, SensorAggregateField, SensorAggregateStatistic } from '../enums'
import { PowerSupply, SensorMeta, Statistic } from '../types'
import Sensor from '../types/Sensor'
import { lessThanMinutesAgo } from './date'

export function calculateKilowatts(sensor: Sensor, statistic: Statistic = 'avg') {
  switch (statistic) {
    case 'min':
      return calculateMinKilowatts(sensor)
    case 'max':
      return calculateMaxKilowatts(sensor)
    case 'avg':
      return calculateAvgKilowatts(sensor)
  }
}

export function calculateMinKilowatts(sensor: Sensor) {
  if (
    sensor.min_current_1 !== null &&
    sensor.min_voltage_1 !== null &&
    sensor.min_current_2 !== null &&
    sensor.min_voltage_2 !== null &&
    sensor.min_current_3 !== null &&
    sensor.min_voltage_3 !== null
  ) {
    return (
      (sensor.min_current_1 * sensor.min_voltage_1 * (sensor.avg_power_factor_1 ?? 1) +
        sensor.min_current_2 * sensor.min_voltage_2 * (sensor.avg_power_factor_2 ?? 1) +
        sensor.min_current_3 * sensor.min_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) /
      1000
    )
  } else if (sensor.min_current_1 !== null && sensor.min_voltage_1 !== null) {
    return (sensor.min_current_1 * sensor.min_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
  } else if (sensor.min_current_2 !== null && sensor.min_voltage_2 !== null) {
    return (sensor.min_current_2 * sensor.min_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
  } else if (sensor.min_current_3 !== null && sensor.min_voltage_3 !== null) {
    return (sensor.min_current_3 * sensor.min_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
  }

  return calculateLegacyKilowatts(sensor)
}

export function calculateMaxKilowatts(sensor: Sensor) {
  if (
    sensor.max_current_1 !== null &&
    sensor.max_voltage_1 !== null &&
    sensor.max_current_2 !== null &&
    sensor.max_voltage_2 !== null &&
    sensor.max_current_3 !== null &&
    sensor.max_voltage_3 !== null
  ) {
    return (
      (sensor.max_current_1 * sensor.max_voltage_1 * (sensor.avg_power_factor_1 ?? 1) +
        sensor.max_current_2 * sensor.max_voltage_2 * (sensor.avg_power_factor_2 ?? 1) +
        sensor.max_current_3 * sensor.max_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) /
      1000
    )
  } else if (sensor.max_current_1 !== null && sensor.max_voltage_1 !== null) {
    return (sensor.max_current_1 * sensor.max_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
  } else if (sensor.max_current_2 !== null && sensor.max_voltage_2 !== null) {
    return (sensor.max_current_2 * sensor.max_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
  } else if (sensor.max_current_3 !== null && sensor.max_voltage_3 !== null) {
    return (sensor.max_current_3 * sensor.max_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
  }

  return calculateLegacyKilowatts(sensor)
}

export function calculateAvgKilowatts(sensor: Sensor) {
  if (
    sensor.avg_current_1 !== null &&
    sensor.avg_voltage_1 !== null &&
    sensor.avg_current_2 !== null &&
    sensor.avg_voltage_2 !== null &&
    sensor.avg_current_3 !== null &&
    sensor.avg_voltage_3 !== null
  ) {
    return (
      (sensor.avg_current_1 * sensor.avg_voltage_1 * (sensor.avg_power_factor_1 ?? 1) +
        sensor.avg_current_2 * sensor.avg_voltage_2 * (sensor.avg_power_factor_2 ?? 1) +
        sensor.avg_current_3 * sensor.avg_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) /
      1000
    )
  } else if (sensor.avg_current_1 !== null && sensor.avg_voltage_1 !== null) {
    return (sensor.avg_current_1 * sensor.avg_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
  } else if (sensor.avg_current_2 !== null && sensor.avg_voltage_2 !== null) {
    return (sensor.avg_current_2 * sensor.avg_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
  } else if (sensor.avg_current_3 !== null && sensor.avg_voltage_3 !== null) {
    return (sensor.avg_current_3 * sensor.avg_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
  }

  return calculateLegacyKilowatts(sensor)
}

function calculateLegacyKilowatts(sensor: Sensor) {
  if (
    sensor.current_1 !== null &&
    sensor.voltage_1 !== null &&
    sensor.current_2 !== null &&
    sensor.voltage_2 !== null &&
    sensor.current_3 !== null &&
    sensor.voltage_3 !== null
  ) {
    return (
      (sensor.current_1 * sensor.voltage_1 +
        sensor.current_2 * sensor.voltage_2 +
        sensor.current_3 * sensor.voltage_3) /
      1000
    )
  } else if (sensor.current_1 !== null && sensor.voltage_1 !== null) {
    return (sensor.current_1 * sensor.voltage_1) / 1000
  } else if (sensor.current_2 !== null && sensor.voltage_2 !== null) {
    return (sensor.current_2 * sensor.voltage_2) / 1000
  } else if (sensor.current_3 !== null && sensor.voltage_3 !== null) {
    return (sensor.current_3 * sensor.voltage_3) / 1000
  }

  return null
}

export function calculatePhaseKilowatts(
  sensor: Sensor,
  phase: Phase,
  statistic: Statistic = 'avg'
) {
  switch (statistic) {
    case 'min':
      return calculateMinPhaseKilowatts(sensor, phase)
    case 'max':
      return calculateMaxPhaseKilowatts(sensor, phase)
    case 'avg':
      return calculateAvgPhaseKilowatts(sensor, phase)
  }
}

export function calculateMinPhaseKilowatts(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.min_current_1 !== null && sensor.min_voltage_1 !== null) {
        return (
          (sensor.min_current_1 * sensor.min_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L2:
      if (sensor.min_current_2 !== null && sensor.min_voltage_2 !== null) {
        return (
          (sensor.min_current_2 * sensor.min_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L3:
      if (sensor.min_current_3 !== null && sensor.min_voltage_3 !== null) {
        return (
          (sensor.min_current_3 * sensor.min_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)
  }
}

export function calculateMaxPhaseKilowatts(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.max_current_1 !== null && sensor.max_voltage_1 !== null) {
        return (
          (sensor.max_current_1 * sensor.max_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L2:
      if (sensor.max_current_2 !== null && sensor.max_voltage_2 !== null) {
        return (
          (sensor.max_current_2 * sensor.max_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L3:
      if (sensor.max_current_3 !== null && sensor.max_voltage_3 !== null) {
        return (
          (sensor.max_current_3 * sensor.max_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)
  }
}

export function calculateAvgPhaseKilowatts(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.avg_current_1 !== null && sensor.avg_voltage_1 !== null) {
        return (
          (sensor.avg_current_1 * sensor.avg_voltage_1 * (sensor.avg_power_factor_1 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L2:
      if (sensor.avg_current_2 !== null && sensor.avg_voltage_2 !== null) {
        return (
          (sensor.avg_current_2 * sensor.avg_voltage_2 * (sensor.avg_power_factor_2 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)

    case Phase.L3:
      if (sensor.avg_current_3 !== null && sensor.avg_voltage_3 !== null) {
        return (
          (sensor.avg_current_3 * sensor.avg_voltage_3 * (sensor.avg_power_factor_3 ?? 1)) / 1000
        )
      }

      return calculateLegacyPhaseKilowatts(sensor, phase)
  }
}

function calculateLegacyPhaseKilowatts(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.current_1 !== null && sensor.voltage_1 !== null) {
        return (sensor.current_1 * sensor.voltage_1) / 1000
      }

      return null

    case Phase.L2:
      if (phase === Phase.L2 && sensor.current_2 !== null && sensor.voltage_2 !== null) {
        return (sensor.current_2 * sensor.voltage_2) / 1000
      }

      return null

    case Phase.L3:
      if (phase === Phase.L3 && sensor.current_3 !== null && sensor.voltage_3 !== null) {
        return (sensor.current_3 * sensor.voltage_3) / 1000
      }

      return null
  }
}

export function calculateKilovoltAmperes(sensor: Sensor, statistic: Statistic = 'avg') {
  switch (statistic) {
    case 'min':
      return calculateMinKilovoltAmperes(sensor)
    case 'max':
      return calculateMaxKilovoltAmperes(sensor)
    case 'avg':
      return calculateAvgKilovoltAmperes(sensor)
  }
}

export function calculateMinKilovoltAmperes(sensor: Sensor) {
  if (
    sensor.min_current_1 !== null &&
    sensor.min_voltage_1 !== null &&
    sensor.min_current_2 !== null &&
    sensor.min_voltage_2 !== null &&
    sensor.min_current_3 !== null &&
    sensor.min_voltage_3 !== null
  ) {
    return (
      (sensor.min_current_1 * sensor.min_voltage_1 +
        sensor.min_current_2 * sensor.min_voltage_2 +
        sensor.min_current_3 * sensor.min_voltage_3) /
      1000
    )
  } else if (sensor.min_current_1 !== null && sensor.min_voltage_1 !== null) {
    return (sensor.min_current_1 * sensor.min_voltage_1) / 1000
  } else if (sensor.min_current_2 !== null && sensor.min_voltage_2 !== null) {
    return (sensor.min_current_2 * sensor.min_voltage_2) / 1000
  } else if (sensor.min_current_3 !== null && sensor.min_voltage_3 !== null) {
    return (sensor.min_current_3 * sensor.min_voltage_3) / 1000
  }

  return calculateLegacyKilovoltAmperes(sensor)
}

export function calculateMaxKilovoltAmperes(sensor: Sensor) {
  if (
    sensor.max_current_1 !== null &&
    sensor.max_voltage_1 !== null &&
    sensor.max_current_2 !== null &&
    sensor.max_voltage_2 !== null &&
    sensor.max_current_3 !== null &&
    sensor.max_voltage_3 !== null
  ) {
    return (
      (sensor.max_current_1 * sensor.max_voltage_1 +
        sensor.max_current_2 * sensor.max_voltage_2 +
        sensor.max_current_3 * sensor.max_voltage_3) /
      1000
    )
  } else if (sensor.max_current_1 !== null && sensor.max_voltage_1 !== null) {
    return (sensor.max_current_1 * sensor.max_voltage_1) / 1000
  } else if (sensor.max_current_2 !== null && sensor.max_voltage_2 !== null) {
    return (sensor.max_current_2 * sensor.max_voltage_2) / 1000
  } else if (sensor.max_current_3 !== null && sensor.max_voltage_3 !== null) {
    return (sensor.max_current_3 * sensor.max_voltage_3) / 1000
  }

  return calculateLegacyKilovoltAmperes(sensor)
}

export function calculateAvgKilovoltAmperes(sensor: Sensor) {
  if (
    sensor.avg_current_1 !== null &&
    sensor.avg_voltage_1 !== null &&
    sensor.avg_current_2 !== null &&
    sensor.avg_voltage_2 !== null &&
    sensor.avg_current_3 !== null &&
    sensor.avg_voltage_3 !== null
  ) {
    return (
      (sensor.avg_current_1 * sensor.avg_voltage_1 +
        sensor.avg_current_2 * sensor.avg_voltage_2 +
        sensor.avg_current_3 * sensor.avg_voltage_3) /
      1000
    )
  } else if (sensor.avg_current_1 !== null && sensor.avg_voltage_1 !== null) {
    return (sensor.avg_current_1 * sensor.avg_voltage_1) / 1000
  } else if (sensor.avg_current_2 !== null && sensor.avg_voltage_2 !== null) {
    return (sensor.avg_current_2 * sensor.avg_voltage_2) / 1000
  } else if (sensor.avg_current_3 !== null && sensor.avg_voltage_3 !== null) {
    return (sensor.avg_current_3 * sensor.avg_voltage_3) / 1000
  }

  return calculateLegacyKilovoltAmperes(sensor)
}

function calculateLegacyKilovoltAmperes(sensor: Sensor) {
  if (
    sensor.current_1 !== null &&
    sensor.voltage_1 !== null &&
    sensor.current_2 !== null &&
    sensor.voltage_2 !== null &&
    sensor.current_3 !== null &&
    sensor.voltage_3 !== null
  ) {
    return (
      (sensor.current_1 * sensor.voltage_1 +
        sensor.current_2 * sensor.voltage_2 +
        sensor.current_3 * sensor.voltage_3) /
      1000
    )
  } else if (sensor.current_1 !== null && sensor.voltage_1 !== null) {
    return (sensor.current_1 * sensor.voltage_1) / 1000
  } else if (sensor.current_2 !== null && sensor.voltage_2 !== null) {
    return (sensor.current_2 * sensor.voltage_2) / 1000
  } else if (sensor.current_3 !== null && sensor.voltage_3 !== null) {
    return (sensor.current_3 * sensor.voltage_3) / 1000
  }

  return null
}

export function calculatePhaseKilovoltAmperes(
  sensor: Sensor,
  phase: Phase,
  statistic: Statistic = 'avg'
) {
  switch (statistic) {
    case 'min':
      return calculateMinPhaseKilovoltAmperes(sensor, phase)
    case 'max':
      return calculateMaxPhaseKilovoltAmperes(sensor, phase)
    case 'avg':
      return calculateAvgPhaseKilovoltAmperes(sensor, phase)
  }
}

export function calculateMinPhaseKilovoltAmperes(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.min_current_1 !== null && sensor.min_voltage_1 !== null) {
        return (sensor.min_current_1 * sensor.min_voltage_1) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L2:
      if (sensor.min_current_2 !== null && sensor.min_voltage_2 !== null) {
        return (sensor.min_current_2 * sensor.min_voltage_2) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L3:
      if (sensor.min_current_3 !== null && sensor.min_voltage_3 !== null) {
        return (sensor.min_current_3 * sensor.min_voltage_3) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)
  }
}

export function calculateMaxPhaseKilovoltAmperes(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.max_current_1 !== null && sensor.max_voltage_1 !== null) {
        return (sensor.max_current_1 * sensor.max_voltage_1) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L2:
      if (sensor.max_current_2 !== null && sensor.max_voltage_2 !== null) {
        return (sensor.max_current_2 * sensor.max_voltage_2) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L3:
      if (sensor.max_current_3 !== null && sensor.max_voltage_3 !== null) {
        return (sensor.max_current_3 * sensor.max_voltage_3) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)
  }
}

export function calculateAvgPhaseKilovoltAmperes(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.avg_current_1 !== null && sensor.avg_voltage_1 !== null) {
        return (sensor.avg_current_1 * sensor.avg_voltage_1) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L2:
      if (sensor.avg_current_2 !== null && sensor.avg_voltage_2 !== null) {
        return (sensor.avg_current_2 * sensor.avg_voltage_2) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)

    case Phase.L3:
      if (sensor.avg_current_3 !== null && sensor.avg_voltage_3 !== null) {
        return (sensor.avg_current_3 * sensor.avg_voltage_3) / 1000
      }

      return calculateLegacyPhaseKilovoltAmperes(sensor, phase)
  }
}

function calculateLegacyPhaseKilovoltAmperes(sensor: Sensor, phase: Phase) {
  switch (phase) {
    case Phase.L1:
      if (sensor.current_1 !== null && sensor.voltage_1 !== null) {
        return (sensor.current_1 * sensor.voltage_1) / 1000
      }

      return null

    case Phase.L2:
      if (phase === Phase.L2 && sensor.current_2 !== null && sensor.voltage_2 !== null) {
        return (sensor.current_2 * sensor.voltage_2) / 1000
      }

      return null

    case Phase.L3:
      if (phase === Phase.L3 && sensor.current_3 !== null && sensor.voltage_3 !== null) {
        return (sensor.current_3 * sensor.voltage_3) / 1000
      }

      return null
  }
}

export function calculateAmps(sensor: Sensor) {
  const power = calculateKilovoltAmperes(sensor)
  const volts = calculateAverageVoltage(sensor)

  if (power === null || volts === null) {
    return null
  }

  return (power * 1000) / volts
}

export function calculateMaxKwh(fuse: number) {
  return Math.round((fuse * 400 * Math.sqrt(3)) / 1000)
}

export function calculateMaxPhaseKwH(fuse: number) {
  return Math.round((fuse * 230) / 1000)
}

export function calculateSumOfCurrents(sensor: Sensor) {
  const l1Current = sensor.avg_current_1 ?? sensor.current_1 ?? null
  const l2Current = sensor.avg_current_2 ?? sensor.current_2 ?? null
  const l3Current = sensor.avg_current_3 ?? sensor.current_3 ?? null

  if (l1Current !== null || l2Current !== null || l3Current !== null) {
    let sum = 0

    if (l1Current !== null) {
      sum += l1Current
    }

    if (l2Current !== null) {
      sum += l2Current
    }

    if (l3Current !== null) {
      sum += l3Current
    }

    return sum
  }

  return null
}

export function calculatePowerUsageAsPercentage(sensor: Sensor, powerSupply: PowerSupply) {
  const sum = calculateSumOfCurrents(sensor)

  if (sum === null) {
    return 0
  }

  const percentage = Math.round((sum / (powerSupply.fuse * 3)) * 100)

  return percentage > 100 ? 100 : percentage
}

export function calculatePhaseKwH(current: number, voltage: number, powerFactor = 1) {
  return Math.round((current * voltage * powerFactor) / 1000)
}

/**
 * Calculate the energy consumption by using the energy counter.
 */
export function calculateEnergyConsumption(sensor: Sensor) {
  if (sensor.energy_counter !== null && sensor.previous_energy_counter !== null) {
    return sensor.energy_counter - sensor.previous_energy_counter
  }

  return calculatePower(sensor)
}

/**
 * Calculate the power for a sensor.
 */
export function calculatePower(
  sensor: Sensor,
  statistic: Statistic | SensorAggregateStatistic = 'avg'
) {
  statistic = statistic as Statistic
  switch (statistic) {
    case 'min':
      return sensor.min_power
    case 'max':
      return sensor.max_power
    case 'avg':
    default:
      return sensor.avg_power
  }
}

/**
 * Calculate the voltage for a sensor.
 */
export function calculateVoltage(
  sensor: Sensor,
  statistic: Statistic | SensorAggregateStatistic = 'avg'
) {
  statistic = statistic as Statistic

  switch (statistic) {
    case 'min':
      return calculateMinimumVoltage(sensor)
    case 'max':
      return calculateMaximumVoltage(sensor)
    case 'avg':
    default:
      return calculateAverageVoltage(sensor)
  }
}

/**
 * Calculate the average voltage for a sensor.
 */
export function calculateAverageVoltage(sensor: Sensor) {
  if (
    sensor.avg_voltage_1 !== null &&
    sensor.avg_voltage_2 !== null &&
    sensor.avg_voltage_3 !== null
  ) {
    return (sensor.avg_voltage_1 + sensor.avg_voltage_2 + sensor.avg_voltage_3) / 3
  } else if (sensor.avg_voltage_1 !== null) {
    return sensor.avg_voltage_1
  } else if (sensor.avg_voltage_2 !== null) {
    return sensor.avg_voltage_2
  } else if (sensor.avg_voltage_3 !== null) {
    return sensor.avg_voltage_3
  }

  if (sensor.voltage_1 !== null && sensor.voltage_2 !== null && sensor.voltage_3 !== null) {
    return (sensor.voltage_1 + sensor.voltage_2 + sensor.voltage_3) / 3
  } else if (sensor.voltage_1 !== null) {
    return sensor.voltage_1
  } else if (sensor.voltage_2 !== null) {
    return sensor.voltage_2
  } else if (sensor.voltage_3 !== null) {
    return sensor.voltage_3
  }

  return null
}

/**
 * Calculate the average voltage for a sensor using the minimum values.
 */
export function calculateMinimumVoltage(sensor: Sensor) {
  if (
    sensor.min_voltage_1 !== null &&
    sensor.min_voltage_2 !== null &&
    sensor.min_voltage_3 !== null
  ) {
    return (sensor.min_voltage_1 + sensor.min_voltage_2 + sensor.min_voltage_3) / 3
  } else if (sensor.min_voltage_1 !== null) {
    return sensor.min_voltage_1
  } else if (sensor.min_voltage_2 !== null) {
    return sensor.min_voltage_2
  } else if (sensor.min_voltage_3 !== null) {
    return sensor.min_voltage_3
  }

  return calculateAverageVoltage(sensor)
}

/**
 * Calculate the average voltage for a sensor using the maximum values.
 */
export function calculateMaximumVoltage(sensor: Sensor) {
  if (
    sensor.max_voltage_1 !== null &&
    sensor.max_voltage_2 !== null &&
    sensor.max_voltage_3 !== null
  ) {
    return (sensor.max_voltage_1 + sensor.max_voltage_2 + sensor.max_voltage_3) / 3
  } else if (sensor.max_voltage_1 !== null) {
    return sensor.max_voltage_1
  } else if (sensor.max_voltage_2 !== null) {
    return sensor.max_voltage_2
  } else if (sensor.max_voltage_3 !== null) {
    return sensor.max_voltage_3
  }

  return calculateAverageVoltage(sensor)
}

/**
 * Calculate the average voltage for a sensor using the median values.
 *
 * @deprecated
 */
export function calculateMedianVoltage(sensor: Sensor) {
  if (
    sensor.med_voltage_1 !== null &&
    sensor.med_voltage_2 !== null &&
    sensor.med_voltage_3 !== null
  ) {
    return (sensor.med_voltage_1 + sensor.med_voltage_2 + sensor.med_voltage_3) / 3
  } else if (sensor.med_voltage_1 !== null) {
    return sensor.med_voltage_1
  } else if (sensor.med_voltage_2 !== null) {
    return sensor.med_voltage_2
  } else if (sensor.med_voltage_3 !== null) {
    return sensor.med_voltage_3
  }

  return calculateAverageVoltage(sensor)
}

/**
 * Calculate the current for a sensor.
 */
export function calculateCurrent(
  sensor: Sensor,
  statistic: Statistic | SensorAggregateStatistic = 'avg'
) {
  statistic = statistic as Statistic

  switch (statistic) {
    case 'min':
      return calculateMinimumCurrent(sensor)
    case 'max':
      return calculateMaximumCurrent(sensor)
    case 'avg':
    default:
      return calculateAverageCurrent(sensor)
  }
}

/**
 * Calculate the average current for a sensor.
 */
export function calculateAverageCurrent(sensor: Sensor) {
  if (
    sensor.avg_current_1 !== null &&
    sensor.avg_current_2 !== null &&
    sensor.avg_current_3 !== null
  ) {
    return (sensor.avg_current_1 + sensor.avg_current_2 + sensor.avg_current_3) / 3
  } else if (sensor.avg_current_1 !== null) {
    return sensor.avg_current_1
  } else if (sensor.avg_current_2 !== null) {
    return sensor.avg_current_2
  } else if (sensor.avg_current_3 !== null) {
    return sensor.avg_current_3
  }

  if (sensor.current_1 !== null && sensor.current_2 !== null && sensor.current_3 !== null) {
    return (sensor.current_1 + sensor.current_2 + sensor.current_3) / 3
  } else if (sensor.current_1 !== null) {
    return sensor.current_1
  } else if (sensor.current_2 !== null) {
    return sensor.current_2
  } else if (sensor.current_3 !== null) {
    return sensor.current_3
  }

  return null
}

/**
 * Calculate the average current for a sensor by using the minimum values.
 */
export function calculateMinimumCurrent(sensor: Sensor) {
  if (
    sensor.min_current_1 !== null &&
    sensor.min_current_2 !== null &&
    sensor.min_current_3 !== null
  ) {
    return (sensor.min_current_1 + sensor.min_current_2 + sensor.min_current_3) / 3
  } else if (sensor.min_current_1 !== null) {
    return sensor.min_current_1
  } else if (sensor.min_current_2 !== null) {
    return sensor.min_current_2
  } else if (sensor.min_current_3 !== null) {
    return sensor.min_current_3
  }

  return calculateAverageCurrent(sensor)
}

/**
 * Calculate the average current for a sensor by using the maximum values.
 */
export function calculateMaximumCurrent(sensor: Sensor) {
  if (
    sensor.max_current_1 !== null &&
    sensor.max_current_2 !== null &&
    sensor.max_current_3 !== null
  ) {
    return (sensor.max_current_1 + sensor.max_current_2 + sensor.max_current_3) / 3
  } else if (sensor.max_current_1 !== null) {
    return sensor.max_current_1
  } else if (sensor.max_current_2 !== null) {
    return sensor.max_current_2
  } else if (sensor.max_current_3 !== null) {
    return sensor.max_current_3
  }

  return calculateAverageCurrent(sensor)
}

/**
 * Calculate the average current for a sensor by using the median values.
 *
 * @deprecated
 */
export function calculateMedianCurrent(sensor: Sensor) {
  if (
    sensor.med_current_1 !== null &&
    sensor.med_current_2 !== null &&
    sensor.med_current_3 !== null
  ) {
    return (sensor.med_current_1 + sensor.med_current_2 + sensor.med_current_3) / 3
  } else if (sensor.med_current_1 !== null) {
    return sensor.med_current_1
  } else if (sensor.med_current_2 !== null) {
    return sensor.med_current_2
  } else if (sensor.med_current_3 !== null) {
    return sensor.med_current_3
  }

  return calculateAverageCurrent(sensor)
}

export function calculateLowestCurrent(sensor: Sensor) {
  if (
    sensor.min_current_1 !== null &&
    sensor.min_current_2 !== null &&
    sensor.min_current_3 !== null
  ) {
    return Math.min(sensor.min_current_1, sensor.min_current_2, sensor.min_current_3)
  } else if (sensor.min_current_1 !== null) {
    return sensor.min_current_1
  } else if (sensor.min_current_2 !== null) {
    return sensor.min_current_2
  } else if (sensor.min_current_3 !== null) {
    return sensor.min_current_3
  }

  if (sensor.current_1 !== null && sensor.current_2 !== null && sensor.current_3 !== null) {
    return Math.min(sensor.current_1, sensor.current_2, sensor.current_3)
  } else if (sensor.current_1 !== null) {
    return sensor.current_1
  } else if (sensor.current_2 !== null) {
    return sensor.current_2
  } else if (sensor.current_3 !== null) {
    return sensor.current_3
  }

  return null
}

export function calculateHighestCurrent(sensor: Sensor) {
  if (
    sensor.max_current_1 !== null &&
    sensor.max_current_2 !== null &&
    sensor.max_current_3 !== null
  ) {
    return Math.max(sensor.max_current_1, sensor.max_current_2, sensor.max_current_3)
  } else if (sensor.max_current_1 !== null) {
    return sensor.max_current_1
  } else if (sensor.max_current_2 !== null) {
    return sensor.max_current_2
  } else if (sensor.max_current_3 !== null) {
    return sensor.max_current_3
  }

  if (sensor.current_1 !== null && sensor.current_2 !== null && sensor.current_3 !== null) {
    return Math.max(sensor.current_1, sensor.current_2, sensor.current_3)
  } else if (sensor.current_1 !== null) {
    return sensor.current_1
  } else if (sensor.current_2 !== null) {
    return sensor.current_2
  } else if (sensor.current_3 !== null) {
    return sensor.current_3
  }

  return null
}

export function calculateLowestVoltage(sensor: Sensor) {
  if (
    sensor.min_voltage_1 !== null &&
    sensor.min_voltage_2 !== null &&
    sensor.min_voltage_3 !== null
  ) {
    return Math.min(sensor.min_voltage_1, sensor.min_voltage_2, sensor.min_voltage_3)
  } else if (sensor.min_voltage_1 !== null) {
    return sensor.min_voltage_1
  } else if (sensor.min_voltage_2 !== null) {
    return sensor.min_voltage_2
  } else if (sensor.min_voltage_3 !== null) {
    return sensor.min_voltage_3
  }

  if (sensor.voltage_1 !== null && sensor.voltage_2 !== null && sensor.voltage_3 !== null) {
    return Math.min(sensor.voltage_1, sensor.voltage_2, sensor.voltage_3)
  } else if (sensor.voltage_1 !== null) {
    return sensor.voltage_1
  } else if (sensor.voltage_2 !== null) {
    return sensor.voltage_2
  } else if (sensor.voltage_3 !== null) {
    return sensor.voltage_3
  }

  return null
}

export function calculateHighestVoltage(sensor: Sensor) {
  if (
    sensor.max_voltage_1 !== null &&
    sensor.max_voltage_2 !== null &&
    sensor.max_voltage_3 !== null
  ) {
    return Math.max(sensor.max_voltage_1, sensor.max_voltage_2, sensor.max_voltage_3)
  } else if (sensor.max_voltage_1 !== null) {
    return sensor.max_voltage_1
  } else if (sensor.max_voltage_2 !== null) {
    return sensor.max_voltage_2
  } else if (sensor.max_voltage_3 !== null) {
    return sensor.max_voltage_3
  }

  if (sensor.voltage_1 !== null && sensor.voltage_2 !== null && sensor.voltage_3 !== null) {
    return Math.max(sensor.voltage_1, sensor.voltage_2, sensor.voltage_3)
  } else if (sensor.voltage_1 !== null) {
    return sensor.voltage_1
  } else if (sensor.voltage_2 !== null) {
    return sensor.voltage_2
  } else if (sensor.voltage_3 !== null) {
    return sensor.voltage_3
  }

  return null
}

export function calculatePowerFactor(sensor: Sensor, statistic: Statistic = 'avg') {
  switch (statistic) {
    case 'min':
      return calculateMinPowerFactor(sensor)
    case 'max':
      return calculateMaxPowerFactor(sensor)
    case 'avg':
    default:
      return calculateAvgPowerFactor(sensor)
  }
}

export function calculateMinPowerFactor(sensor: Sensor) {
  if (
    sensor.min_power_factor_1 !== null &&
    sensor.min_power_factor_2 !== null &&
    sensor.min_power_factor_3 !== null
  ) {
    return (sensor.min_power_factor_1 + sensor.min_power_factor_2 + sensor.min_power_factor_3) / 3
  } else if (sensor.min_power_factor_1 !== null) {
    return sensor.min_power_factor_1
  } else if (sensor.min_power_factor_2 !== null) {
    return sensor.min_power_factor_2
  } else if (sensor.min_power_factor_3 !== null) {
    return sensor.min_power_factor_3
  }

  return null
}

export function calculateMaxPowerFactor(sensor: Sensor) {
  if (
    sensor.max_power_factor_1 !== null &&
    sensor.max_power_factor_2 !== null &&
    sensor.max_power_factor_3 !== null
  ) {
    return (sensor.max_power_factor_1 + sensor.max_power_factor_2 + sensor.max_power_factor_3) / 3
  } else if (sensor.max_power_factor_1 !== null) {
    return sensor.max_power_factor_1
  } else if (sensor.max_power_factor_2 !== null) {
    return sensor.max_power_factor_2
  } else if (sensor.max_power_factor_3 !== null) {
    return sensor.max_power_factor_3
  }

  return null
}

export function calculateAvgPowerFactor(sensor: Sensor) {
  if (
    sensor.avg_power_factor_1 !== null &&
    sensor.avg_power_factor_2 !== null &&
    sensor.avg_power_factor_3 !== null
  ) {
    return (sensor.avg_power_factor_1 + sensor.avg_power_factor_2 + sensor.avg_power_factor_3) / 3
  } else if (sensor.avg_power_factor_1 !== null) {
    return sensor.avg_power_factor_1
  } else if (sensor.avg_power_factor_2 !== null) {
    return sensor.avg_power_factor_2
  } else if (sensor.avg_power_factor_3 !== null) {
    return sensor.avg_power_factor_3
  }

  return null
}

/**
 * Check if the sensor was recently measured.
 */
export function isSensorNow(sensor: Sensor): boolean {
  return lessThanMinutesAgo(new Date(sensor.time), 5)
}

/**
 * Get the color of the sensor field.
 */
export const getSensorFieldColor = (
  field: SensorAggregateField,
  statistic?: SensorAggregateStatistic
) => {
  let weight: 'A100' | '500' | 'A700' = '500'
  switch (statistic) {
    case SensorAggregateStatistic.MIN:
      weight = 'A100'
      break
    case SensorAggregateStatistic.MAX:
      weight = 'A700'
      break
    case SensorAggregateStatistic.AVG:
    default:
      weight = '500'
      break
  }

  switch (field) {
    case SensorAggregateField.CURRENT_1:
      return blue[weight]
    case SensorAggregateField.CURRENT_2:
      return pink[weight]
    case SensorAggregateField.CURRENT_3:
      return orange[weight]
    case SensorAggregateField.VOLTAGE_1:
      return deepPurple[weight]
    case SensorAggregateField.VOLTAGE_2:
      return blueGrey[weight]
    case SensorAggregateField.VOLTAGE_3:
      return amber[weight]
    case SensorAggregateField.POWER:
      return purple[weight]
    case SensorAggregateField.POWER_1:
      return indigo[weight]
    case SensorAggregateField.POWER_2:
      return cyan[weight]
    case SensorAggregateField.POWER_3:
      return blueGrey[weight]
    case SensorAggregateField.POWER_FACTOR_1:
      return indigo[weight]
    case SensorAggregateField.POWER_FACTOR_2:
      return cyan[weight]
    case SensorAggregateField.POWER_FACTOR_3:
      return blue[weight]
    case SensorAggregateField.FREQUENCY_FACTOR_1:
      return red[weight]
    case SensorAggregateField.FREQUENCY_FACTOR_2:
      return green[weight]
    case SensorAggregateField.FREQUENCY_FACTOR_3:
      return blue[weight]
    case SensorAggregateField.RATED_CURRENT:
      return yellow[weight]
    case SensorAggregateField.TEMP:
    case SensorAggregateField.WEATHER_TEMPERATURE:
    case SensorAggregateField.ATTACHED_TEMPERATURE:
    case SensorAggregateField.PROJECT_OUTSIDE_TEMPERATURE:
      return cyan[weight]
    case SensorAggregateField.RH:
    case SensorAggregateField.WEATHER_HUMIDITY:
    case SensorAggregateField.ATTACHED_HUMIDITY:
    case SensorAggregateField.PROJECT_OUTSIDE_HUMIDITY:
      return teal[weight]
    case SensorAggregateField.RSSI:
      return lime[weight]
    case SensorAggregateField.ENERGY_PRICE:
    case SensorAggregateField.ENERGY_SPOT_PRICE:
    case SensorAggregateField.ENERGY_DISTRIBUTION_PRICE:
      return green[weight]
    default:
      return blue[weight]
  }
}

/**
 * The threshold for low current (A).
 */
export const LOW_CURRENT = 1

/**
 * The threshold for low voltage (V).
 */
export const LOW_VOLTAGE = 225

/**
 * The threshold for high voltage (V).
 */
export const HIGH_VOLTAGE = 240

/**
 * The threshold for low power (kW).
 */
export const LOW_POWER = 1

/**
 * Checks if the current is below the threshold.
 */
export const isLowCurrent = (current: number) => {
  return current < LOW_CURRENT
}

/**
 * Checks if the voltage is outside of threshold.
 */
export const isLowOrHighVoltage = (voltage: number) => {
  return voltage < LOW_VOLTAGE || voltage > HIGH_VOLTAGE
}

/**
 * Checks if the power is below the threshold.
 */
export const isLowPower = (power: number) => {
  return power < LOW_POWER
}

export function isSensorIncomplete(sensor: Sensor) {
  return (
    isSensorMissingEnergyPrice(sensor) ||
    isSensorMissingEmissionFactor(sensor) ||
    isSensorMissingWeatherObservation(sensor)
  )
}

function isSensorMissingEnergyPrice(sensor: Sensor) {
  return sensor.energy_spot_price === null
}

function isSensorMissingEmissionFactor(sensor: Sensor) {
  return sensor.emission_factor === null
}

function isSensorMissingWeatherObservation(sensor: Sensor) {
  return (
    sensor.weather_temperature === null ||
    sensor.weather_humidity === null ||
    sensor.weather_pressure === null ||
    sensor.weather_wind_speed === null
  )
}

export function isSensorMetaIncomplete(meta: SensorMeta) {
  return (
    isSensorMetaMissingEnergyPrice(meta) ||
    isSensorMetaMissingEmissionFactor(meta) ||
    isSensorMetaMissingWeatherObservation(meta)
  )
}

export function isSensorMetaMissingEnergyPrice(meta: SensorMeta) {
  return meta.energy_price_spot === null
}

export function isSensorMetaMissingEmissionFactor(meta: SensorMeta) {
  return meta.emission_factor_realtime === null
}

export function isSensorMetaMissingWeatherObservation(meta: SensorMeta) {
  return (
    meta.weather_temperature === null ||
    meta.weather_humidity === null ||
    meta.weather_pressure === null ||
    meta.weather_wind_speed === null ||
    meta.weather_feels_like_temperature === null
  )
}

export function isSensor(value: unknown): value is Sensor {
  const fields = [
    'time',
    'current_1',
    'current_2',
    'current_3',
    'voltage_1',
    'voltage_2',
    'voltage_3'
  ]

  return typeof value === 'object' && value !== null && fields.every((field) => field in value)
}
